<template>
  <div class="user-card" :class="{ 'is-IE': isIE11 }">
    <div class="user-header">
      <img @click="viewProfile(user.userName)" :src="proxy(user.avatar)">
      <span class="usernames cursor-pointer" @click="viewProfile(user.userName)" v-line-clamp="3">
        <b>{{ user.firstName }} {{ user.lastName }}
        </b>
        <p @click="viewProfile(user.userName)">@{{ user.userName }}
          <span><i class="fal" v-bind:class="`fa-${user.badgeImage}`"></i></span>
        </p>
      </span>
    </div>
    <div class="engagement" v-if="profile.counts">
      <span v-bind="tooltipFollowers"><i class="fal fa-user-friends"></i>{{ profile.counts['followers'] }}</span>
      <span v-bind="tooltipProjects"><i class="fal fa-wrench"></i>{{ profile.counts['projects'] }}</span>
      <span v-bind="tooltipEngagement"><i class="fal fa-fingerprint"></i>{{ user.metadata.engagement}}</span>
    </div>
    <div class="user-card-location" v-if="profile.location && profile.location != ''">
      <i class="fal fa-map-marker-alt"></i>{{ profile.location }}
    </div>
    <html-view v-line-clamp="lineClampAmount()" autoresize="true" class="blurb-span bio" ref="htmlView" :content="blurb"></html-view>
    <div class="follow-button" v-if="currentUser.userName != user.userName"><button class="button is-small" :class="{ 'is-primary has-text-white' : !followingUser }" @click="toggleConnection">{{ label }}</button></div>
    <modal-custom
        v-if="showDisconnectModal"
        class="custom-modal"
        :hasFooter="true"
        :title="$t('userCard.unfollow')" :text="$t('userCard.unfollowMessage')"
        :successText="$t('userCard.unfollow')" :cancelText="$t('cancel')"
        @modalSuccess="confirmDisconnect" @modalCancel="showDisconnectModal = false"
    ></modal-custom>
    <fade-message :message="message" :error="error" @clearMessage="message = ''" @clearError="error = ''"></fade-message>
  </div>
</template>

<script>
import Vue from 'vue'
import lineClamp from 'vue-line-clamp'

import userAgent from '@/libs/user-agent'
import imageProxy from '@/libs/image-proxy'
import HtmlView from '@/components/Controls/HTMLView'
import subscribe from '@/mixins/subscribe'
import auth from '@/mixins/auth'

import ModalCustom from '@/components/Modals/Custom'
import FadeMessage from '@/components/Controls/FadeMessage'

import mixin from '../header'

import throttle from 'lodash/throttle'
import i18n from '@/i18n'

Vue.use(lineClamp, {
  textOverflow: 'ellipsis'
})

export default {
  components: {
    ModalCustom,
    FadeMessage,
    HtmlView
  },
  props: ['user'],
  mixins: [mixin, auth, subscribe],
  data: function () {
    return {
      blurb: '',
      profile: [],
      followingUser: false,
      showDisconnectModal: false,
      message: '',
      error: ''
    }
  },
  created: function () {
    this.fetchBlurb()
  },
  mounted: function () {
    this.fetchProfile()
    this.checkConnection()
  },
  computed: {
    isIE11: function () {
      return userAgent.isIE11()
    },
    label: function () {
      return !this.followingUser ? i18n.t('userCard.follow') : i18n.t('userCard.following')
    },
    tooltipFollowers: function () {
      const tooltip = {}
      if (!this.isMobile) {
        tooltip['data-balloon'] = i18n.t('userCard.follow')
        tooltip['data-balloon-pos'] = 'down'
      }
      return tooltip
    },
    tooltipProjects: function () {
      const tooltip = {}
      if (!this.isMobile) {
        tooltip['data-balloon'] = i18n.t('userCard.projects')
        tooltip['data-balloon-pos'] = 'down'
      }
      return tooltip
    },
    tooltipEngagement: function () {
      const tooltip = {}
      if (!this.isMobile) {
        tooltip['data-balloon'] = i18n.t('userCard.engagement')
        tooltip['data-balloon-pos'] = 'down'
      }
      return tooltip
    }
  },
  methods: {
    proxy: function (src) {
      return imageProxy.proxy(src, { width: 45, height: 45 })
    },
    randomMessage: function () {
      if (this.$isEngCom) { // Serious message for eng.com
        return this.user.userName + " hasn't set up their profile yet"
      }
    },
    fetchBlurb: function () {
      this.$store.dispatch('getProfile', this.user.userName).then((user) => {
        this.blurb = user.intro || user.bio || this.randomMessage()
      })
    },
    fetchProfile: function () {
      this.$store.dispatch('getProfile', this.user.userName).then((profile) => {
        this.profile = profile
        this.getProfileCounts()
      })
    },
    getProfileCounts: function () {
      const filter = ''
      this.$store.dispatch('getProfileCounts', { username: this.user.userName, filter })
    },
    lineClampAmount: function () {
      if (this.$isIsef || (this.$isYSC && this.$route.fullPath.includes('expobooth'))) {
        return 3
      } else {
        return 4
      }
    },
    checkConnection: function () {
      if (this.isLoggedIn) {
        this.followingUser = false
        this.$store.dispatch('checkForUserConnection', { username: this.currentUser.userName, otherUsername: this.user.userName }).then((following) => {
          this.followingUser = following
        }, (error) => {
          this.error = error.message
        })
      }
    },
    toggleConnection: function () {
      if (this.followingUser) {
        return this.disconnect()
      }
      this.connect()
    },
    connect: throttle(function () {
      if (!this.isLoggedIn) {
        return this.$store.dispatch('showLoginModal', { source: 'CONNECT:{PATH}', onSuccess: this.userConnect, signupMessage: 'Sign up to connect with ' + this.user.userName })
      }
      this.userConnect()
    }, 500, { leading: true, trailing: false }),
    userConnect: function () {
      this.error = ''
      this.$store.dispatch('connectToUser', { username: this.currentUser.userName, otherUsername: this.user.userName }).then((response) => {
        this.message = `You are now following ${this.user.userName}`
        this.followingUser = true
        this.getProfileCounts()
      }, (error) => {
        this.error = error.message
      })
    },
    disconnect: function () {
      this.showDisconnectModal = true
    },
    confirmDisconnect: throttle(function () {
      this.showDisconnectModal = false
      this.error = ''
      this.$store.dispatch('disconnectFromUser', { username: this.currentUser.userName, otherUsername: this.user.userName }).then((response) => {
        this.message = `You have unfollowed ${this.user.userName}`
        this.followingUser = false
        this.getProfileCounts()
      }, (error) => {
        this.error = error.message
      })
    }, 500, { leading: true, trailing: false })
  }
}
</script>

<style lang="scss" scoped>
@import '~styles/utils.scss';

.blurb-span {
  display: inline-block;
  max-height: 5rem;
  overflow: hidden;
  padding-bottom: 0.2rem;
}

.user-card {
  width: 215px;
  font-size: 0.8rem;
  z-index: 10;
  background-color: white;
  padding: 1rem;
  color: #4A4A4A;
  margin-bottom: 1rem;

@media only screen
and (max-height: 430px)
and (orientation: landscape) {
  background-color: transparent !important;
  margin-top: .25rem;
  margin-left: -1.1rem;
}

div {
  margin: 10px 0;
}

.custom-modal {
  margin: 0;
}

i {
  margin-right: 5px;
}

.engagement {
  display: flex;
  justify-content: space-between;
span, .fas {
  cursor: default;
}
}

.user-header {
  display: flex;
  align-items: center;
img {
  cursor: pointer;
  min-width: 45px;
  max-width: 45px;
  min-height: 45px;
  max-height: 45px;
  margin-right: 10px;
  border-radius: 25px;
}
// Hide the social media when landscaped
@media only screen
and (max-height: 430px)
and (orientation: landscape) {
  display: none;
}
}

.usernames {
  overflow: hidden;
  text-overflow: ellipsis;
  height: 4.7em;
  word-break: break-word !important;
p {
  overflow: hidden;
  text-overflow: ellipsis;
}
}

.blurb {
  width: 168px;
  margin: 0 auto;
  word-break: keep-all !important;
}

.button {
  text-transform: uppercase;
  width: 100%;
  font-weight: 700;
  color: $primary;
  border: 1px solid $primary;
&:hover {
   color: white;
   border-color: $link-hover;
   background-color: $link-hover;
 }
}
}

.is-IE {
  right: calc(100vw/2 - 400px - 200px - 16px);
}

</style>
